/**
 * Kennedy Blue - Product Demand Validation System
 *
 * Handles:
 * - 5-signal survey collection (intent, price, disappointment, problem, commitment)
 * - Klaviyo email subscription with product interest tagging
 * - $1 deposit via Shopify cart
 * - Google Sheets webhook for data aggregation
 * - Shopify metafield updates via Storefront API
 * - Demand score algorithm
 *
 * Dependencies: Klaviyo JS SDK (already loaded in theme.liquid)
 */

(function() {
  'use strict';

  var config = window.__voteConfig || {};
  var form = document.getElementById('js-vote-form');
  if (!form) return;

  // Prevent native form submission entirely — stops theme JS and apps
  // from intercepting the submit event and redirecting to checkout
  form.setAttribute('action', 'javascript:void(0)');
  form.setAttribute('method', 'dialog');

  // =========================================================================
  // STATE
  // =========================================================================
  var state = {
    intentScore: 0,
    intentTouched: false,
    expectedPrice: null,
    disappointment: null,
    wouldRecommend: null,
    problemStatement: '',
    email: '',
    emailSubmitted: false,
    depositClicked: false,
    voteSubmitted: false
  };

  // =========================================================================
  // INTENT SLIDER
  // =========================================================================
  var intentSlider = document.getElementById('js-intent-slider');
  var intentValue = document.getElementById('js-intent-value');
  var intentDescription = document.getElementById('js-intent-description');

  var intentDescriptions = {
    0: 'No interest at all',
    1: 'Very unlikely',
    2: 'Unlikely',
    3: 'Probably not',
    4: 'Slight chance',
    5: 'I might consider it',
    6: 'Leaning towards yes',
    7: 'I\'d likely buy this',
    8: 'I really want this',
    9: 'I\'d buy this for sure',
    10: 'Take my money now!'
  };

  if (intentSlider) {
    intentSlider.addEventListener('input', function() {
      var val = parseInt(this.value, 10);
      state.intentScore = val;
      state.intentTouched = true;
      if (intentValue) intentValue.textContent = val;
      if (intentDescription) intentDescription.textContent = intentDescriptions[val] || '';
      // A11Y Sep 2026: spoken value matches the visible one
      this.setAttribute('aria-valuetext', val + ' out of 10' + (intentDescriptions[val] ? ', ' + intentDescriptions[val] : ''));

      // Update slider fill visual
      var percent = (val / 10) * 100;
      this.style.setProperty('--slider-percent', percent + '%');

      // Clear error state on interaction
      var intentItem = intentSlider.closest('.vote-item');
      if (intentItem) intentItem.classList.remove('vote-item--error');
    });

    // Set initial fill at 0
    intentSlider.style.setProperty('--slider-percent', '0%');
  }

  // =========================================================================
  // PRICE INPUT
  // =========================================================================
  var priceInput = document.getElementById('js-price-input');
  if (priceInput) {
    priceInput.addEventListener('input', function() {
      var val = parseFloat(this.value);
      if (!isNaN(val) && val > 0) {
        state.expectedPrice = val;
      }
    });
  }

  // =========================================================================
  // DISAPPOINTMENT RADIOS
  // =========================================================================
  var disappointmentRadios = form.querySelectorAll('input[name="disappointment"]');
  disappointmentRadios.forEach(function(radio) {
    radio.addEventListener('change', function() {
      state.disappointment = this.value;
    });
  });

  // =========================================================================
  // RECOMMEND RADIOS
  // =========================================================================
  var recommendRadios = form.querySelectorAll('input[name="would_recommend"]');
  recommendRadios.forEach(function(radio) {
    radio.addEventListener('change', function() {
      state.wouldRecommend = this.value;
    });
  });

  // =========================================================================
  // PROBLEM TEXTAREA
  // =========================================================================
  var problemInput = document.getElementById('js-problem-input');
  if (problemInput) {
    problemInput.addEventListener('input', function() {
      state.problemStatement = this.value;
    });
  }

  // =========================================================================
  // DEMAND BAR
  // =========================================================================
  function updateDemandBar() {
    var total = config.currentVotes || 0;
    var goal = config.goalVotes || 100;
    var percent = Math.min(Math.round((total / goal) * 100), 100);

    var fillEl = document.getElementById('js-demand-fill');
    var percentEl = document.getElementById('js-demand-percent');
    var goalEl = document.getElementById('js-demand-goal');
    var countEl = document.getElementById('js-vote-count');

    if (fillEl) fillEl.style.width = percent + '%';
    if (percentEl) percentEl.textContent = percent + '%';
    if (goalEl) goalEl.textContent = total + ' of ' + goal + ' votes to production';
    if (countEl) countEl.textContent = total;
  }

  updateDemandBar();

  // =========================================================================
  // EMAIL SIGNUP (KLAVIYO)
  // =========================================================================
  var emailInput = document.getElementById('js-email-input');
  var emailSuccess = document.getElementById('js-email-success');

  function subscribeToKlaviyo(email, callback) {
    var listId = config.klaviyoListId;
    var companyId = config.klaviyoCompanyId;

    // If no company ID configured, skip the API call
    if (!companyId) {
      console.warn('Vote System: No Klaviyo company ID configured. Email stored locally only.');
      callback(true);
      return;
    }

    // Method 1: Use Klaviyo onsite JS SDK (preferred, already loaded)
    if (window.klaviyo && typeof window.klaviyo.push === 'function') {
      window.klaviyo.push(['identify', {
        email: email,
        'Product Interest': config.productTitle,
        'Product Interest Handle': config.productHandle
      }]);
    }

    // Method 2: Use Klaviyo Client API directly for list subscription
    var data = {
      type: 'subscription',
      attributes: {
        custom_source: 'Product Vote - ' + config.productTitle,
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email: email,
              properties: {
                'Voted Products': config.productHandle,
                'Last Voted Product': config.productTitle,
                'Vote Source': 'demand-validator'
              }
            }
          }
        }
      }
    };

    if (listId) {
      data.relationships = {
        list: {
          data: {
            type: 'list',
            id: listId
          }
        }
      };
    }

    // Use Klaviyo Client API v3
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + companyId, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-02-15'
      },
      body: JSON.stringify({ data: data })
    })
    .then(function(res) {
      // 202 = accepted, which is success for Klaviyo
      callback(res.status === 202 || res.ok);
    })
    .catch(function(err) {
      console.warn('Vote System: Klaviyo request failed -', err.message);
      callback(false);
    });
  }

  function trackKlaviyoEvent(eventName, properties) {
    if (window.klaviyo && typeof window.klaviyo.push === 'function') {
      window.klaviyo.push(['track', eventName, properties]);
    }

    // Also use the Klaviyo Client API
    fetch('https://a.klaviyo.com/client/events/?company_id=' + config.klaviyoCompanyId, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-02-15'
      },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: eventName } } },
            profile: {
              data: {
                type: 'profile',
                attributes: state.email ? { email: state.email } : { anonymous_id: getAnonId() }
              }
            },
            properties: properties
          }
        }
      })
    }).catch(function() { /* silent fail for tracking */ });
  }

  // =========================================================================
  // $1 DEPOSIT (ADD TO CART)
  // =========================================================================
  var depositBtn = document.getElementById('js-deposit-btn');

  if (depositBtn) {
    depositBtn.addEventListener('click', function() {
      var handle = this.getAttribute('data-deposit-handle');
      if (!handle) {
        console.error('Vote System: No deposit product handle configured');
        return;
      }

      depositBtn.disabled = true;
      depositBtn.textContent = 'Adding to cart...';

      // Fetch the deposit product to get variant ID
      fetch('/products/' + handle + '.js')
        .then(function(res) {
          if (!res.ok) throw new Error('Deposit product not found. Create a $1 product with handle: ' + handle);
          return res.json();
        })
        .then(function(product) {
          var variantId = product.variants[0].id;

          // Add to cart with product vote metadata
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: 1,
                properties: {
                  '_vote_product': config.productTitle,
                  '_vote_product_handle': config.productHandle,
                  '_vote_product_id': String(config.productId),
                  'Reservation For': config.productTitle
                }
              }]
            })
          });
        })
        .then(function(res) {
          if (!res.ok) throw new Error('Failed to add deposit to cart');
          return res.json();
        })
        .then(function() {
          state.depositClicked = true;
          depositBtn.textContent = 'Added! Going to cart...';
          depositBtn.classList.add('vote-deposit__btn--success');

          // Track deposit as strongest signal
          trackKlaviyoEvent('Product Vote - $1 Deposit', {
            ProductName: config.productTitle,
            ProductHandle: config.productHandle,
            ProductURL: config.productUrl,
            Value: 1
          });

          // Redirect to cart after short delay
          setTimeout(function() {
            window.location.href = '/cart';
          }, 1000);
        })
        .catch(function(err) {
          console.error('Vote System: Deposit error -', err.message);
          depositBtn.disabled = false;
          depositBtn.textContent = 'Reserve for $1 →';
          alert('Could not add reservation. Please check that the deposit product exists. Error: ' + err.message);
        });
    });
  }

  // =========================================================================
  // FORM SUBMISSION (Vote data)
  // =========================================================================
  var submitBtn = document.getElementById('js-vote-submit');
  var successEl = document.getElementById('js-vote-success');

  // Block Zipify OneClickUpsell (and any other app) from intercepting
  // the submit button click and redirecting to checkout.
  // Button type changed to 'button' so no native form submit fires.
  // All vote logic handled directly in the click handler below.
  if (submitBtn) {
    submitBtn.type = 'button';
    submitBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      e.stopImmediatePropagation();
      handleVoteSubmit();
    }, true); // capture phase — runs before any app listener
  }

  // Also block native form submit as a safety net
  if (form) {
    form.addEventListener('submit', function(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    });
  }

  // =========================================================================
  // REQUIRED FIELD VALIDATION
  // =========================================================================
  function validateRequiredFields() {
    var valid = true;

    // Clear previous error states
    var errorItems = form.querySelectorAll('.vote-item--error');
    errorItems.forEach(function(el) { el.classList.remove('vote-item--error'); });
    var errorMsgs = form.querySelectorAll('.vote-field-error--visible');
    errorMsgs.forEach(function(el) { el.classList.remove('vote-field-error--visible'); });

    var requiredItems = form.querySelectorAll('[data-required="true"]');
    requiredItems.forEach(function(item) {
      var step = item.getAttribute('data-step');
      var missing = false;

      if (step === 'intent') {
        // Slider starts at 0 — user must move it at least once
        missing = !state.intentTouched;
      } else if (step === 'price') {
        missing = !state.expectedPrice;
      } else if (step === 'disappointment') {
        missing = !state.disappointment;
      } else if (step === 'problem') {
        missing = !state.problemStatement || !state.problemStatement.trim();
      } else if (step === 'email') {
        var emailVal = emailInput ? emailInput.value.trim() : '';
        missing = !emailVal || !isValidEmail(emailVal);
      }

      if (missing) {
        valid = false;
        item.classList.add('vote-item--error');
        shakeElement(item);
      }
    });

    return valid;
  }

  function handleVoteSubmit() {
      if (state.voteSubmitted) return;

      // Validate required fields before proceeding
      if (!validateRequiredFields()) return;

      // Capture email from input if user typed it
      if (!state.email && emailInput) {
        var typedEmail = emailInput.value.trim();
        if (typedEmail && isValidEmail(typedEmail)) {
          state.email = typedEmail;
        }
      }

      // Build vote payload
      var payload = {
        product_id: config.productId,
        product_handle: config.productHandle,
        product_title: config.productTitle,
        intent_score: state.intentScore,
        expected_price: state.expectedPrice,
        disappointment: state.disappointment,
        would_recommend: state.wouldRecommend,
        problem_statement: state.problemStatement,
        email: state.email,
        email_submitted: state.emailSubmitted,
        deposit_clicked: state.depositClicked,
        timestamp: new Date().toISOString(),
        anonymous_id: getAnonId(),
        demand_score: calculateDemandScore(state)
      };

      // Show success immediately -- do not wait for network calls
      state.voteSubmitted = true;
      markAsVoted(config.productHandle);
      showSuccess();
      config.currentVotes++;
      updateDemandBar();

      // Fire all network calls in background (non-blocking)
      // Google Sheets
      if (config.googleSheetsWebhook) {
        sendToGoogleSheets(payload);
      }

      // Klaviyo event tracking
      trackKlaviyoEvent('Product Vote Submitted', {
        ProductName: config.productTitle,
        ProductHandle: config.productHandle,
        IntentScore: state.intentScore,
        ExpectedPrice: state.expectedPrice,
        Disappointment: state.disappointment,
        DemandScore: payload.demand_score,
        EmailProvided: !!state.email,
        DepositClicked: state.depositClicked
      });

      // Subscribe email to Klaviyo if provided (even if Notify Me was not clicked)
      if (state.email && !state.emailSubmitted) {
        subscribeToKlaviyo(state.email, function() { /* fire and forget */ });
      }
  }

  function showSuccess() {
    // Hide form steps
    var steps = form.querySelectorAll('.vote-group, .vote-submit');
    steps.forEach(function(step) { step.style.display = 'none'; });

    // If email was captured, show the email success state too
    if (state.email && emailSuccess) {
      emailSuccess.style.display = 'flex';
    }

    // Show success message
    if (successEl) successEl.style.display = 'block';
  }

  // =========================================================================
  // DEMAND SCORE ALGORITHM
  // =========================================================================
  /**
   * Calculates a 0-100 demand score based on weighted signals.
   *
   * Weights (from research):
   * - $1 Deposit:         35% (behavioral commitment, 30x more predictive)
   * - Price Alignment:    25% (prevents #1 failure: wrong price)
   * - Disappointment:     20% (Sean Ellis PMF proxy, 40% threshold)
   * - Purchase Intent:    15% (stated preference, ~50% accuracy alone)
   * - Email Signup:        5% (lowest commitment signal)
   */
  function calculateDemandScore(s) {
    var score = 0;

    // Deposit: 35 points (binary - did they or didn't they)
    if (s.depositClicked) {
      score += 35;
    }

    // Price alignment: 25 points
    // Compare expected price to actual product price
    if (s.expectedPrice && config.productPrice) {
      var actualPrice = config.productPrice / 100; // Shopify prices in cents
      var ratio = s.expectedPrice / actualPrice;

      if (ratio >= 0.9 && ratio <= 1.5) {
        // Within 90%-150% of target = full score
        score += 25;
      } else if (ratio >= 0.7 && ratio < 0.9) {
        // 70-90% = partial score (they'd pay less but close)
        score += 15;
      } else if (ratio > 1.5) {
        // They'd pay MORE than target = also full score
        score += 25;
      } else {
        // Below 70% = price misalignment
        score += 5;
      }
    }

    // Disappointment: 20 points
    if (s.disappointment === 'very_disappointed') {
      score += 20;
    } else if (s.disappointment === 'somewhat_disappointed') {
      score += 10;
    }
    // 'not_disappointed' = 0 points

    // Intent: 15 points (scaled from 0-10 slider)
    score += Math.round((s.intentScore / 10) * 15);

    // Email: 5 points (captured via input or submitted via old handler)
    if (s.emailSubmitted || s.email) {
      score += 5;
    }

    return Math.min(score, 100);
  }

  // =========================================================================
  // DATA DESTINATIONS
  // =========================================================================

  /**
   * Send vote data to Google Sheets via Apps Script web app.
   * Set up a Google Apps Script that receives POST requests and
   * appends rows to a spreadsheet.
   */
  function sendToGoogleSheets(payload) {
    return fetch(config.googleSheetsWebhook, {
      method: 'POST',
      mode: 'no-cors', // Google Apps Script requires no-cors from browser
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).catch(function() {
      return Promise.resolve(); // Non-critical
    });
  }

  // =========================================================================
  // UTILITIES
  // =========================================================================

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function getAnonId() {
    var key = 'kb_vote_anon_id';
    var id = localStorage.getItem(key);
    if (!id) {
      id = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      try { localStorage.setItem(key, id); } catch(e) {}
    }
    return id;
  }

  function markAsVoted(handle) {
    var key = 'kb_voted_products';
    var voted = [];
    try { voted = JSON.parse(localStorage.getItem(key) || '[]'); } catch(e) {}
    if (voted.indexOf(handle) === -1) {
      voted.push(handle);
      try { localStorage.setItem(key, JSON.stringify(voted)); } catch(e) {}
    }
  }

  function hasVoted(handle) {
    var key = 'kb_voted_products';
    try {
      var voted = JSON.parse(localStorage.getItem(key) || '[]');
      return voted.indexOf(handle) !== -1;
    } catch(e) { return false; }
  }

  function shakeElement(el) {
    if (!el) return;
    el.classList.add('vote-shake');
    setTimeout(function() { el.classList.remove('vote-shake'); }, 600);
  }

  // =========================================================================
  // DUPLICATE VOTE CHECK
  // =========================================================================
  if (hasVoted(config.productHandle)) {
    // Show a "you already voted" state instead of the form
    var formSteps = form.querySelectorAll('.vote-group, .vote-submit');
    formSteps.forEach(function(step) { step.style.display = 'none'; });
    if (successEl) {
      successEl.style.display = 'block';
      var successTitle = successEl.querySelector('.vote-success__title');
      if (successTitle) successTitle.textContent = config.alreadyVotedHeading || 'You\'ve already voted on this product!';
      var successMsg = successEl.querySelector('.vote-success__message');
      if (successMsg) successMsg.textContent = config.alreadyVotedMessage || 'Thanks for your interest. We\'ll keep you posted on this product\'s progress.';
    }
  }

})();
